import {AxiosResponse} from 'axios';

import tokenInstance from 'utils/axiosInstance/tokenInstance';
import {IGroupRoom} from 'models/call/types';

const handleGetRoomToken = (): Promise<AxiosResponse<any, any>> => {
    return tokenInstance().get('/rooms/temp-get-token');
};

const getPrivateRooms = (): Promise<AxiosResponse<any, any>> => {
    return tokenInstance().get('/rooms/private/');
};

const getPublicRooms = (): Promise<AxiosResponse<any, any>> => {
    return tokenInstance().get('/rooms/public/');
};

const postPublicRooms = ({
    name,
}: {
    name: string;
}): Promise<AxiosResponse<any, any>> => {
    return tokenInstance().post('/rooms/public/', {name});
};

const patchPublicRooms = ({
    name,
    roomId,
}: {
    name: string;
    roomId: string;
}): Promise<AxiosResponse<any, any>> => {
    return tokenInstance().patch(`/rooms/public/${roomId}`, {name});
};

const patchPrivateRooms = ({
    name,
    roomId,
}: {
    name: string;
    roomId: string;
}): Promise<AxiosResponse<any, any>> => {
    return tokenInstance().patch(`/rooms/private/${roomId}`, {name});
};

const postPrivateRooms = ({
    name,
}: {
    name: string;
}): Promise<AxiosResponse<any, any>> => {
    return tokenInstance().post('/rooms/private/', {name});
};

export const getCompanyLobbyRoomToken = ({
    roomId,
    companyId,
}: {
    roomId: string;
    companyId: string;
}): Promise<AxiosResponse<any, any>> => {
    return tokenInstance().get(
        `companies/${companyId}/rooms/${roomId}/lobby-token`,
    );
};

export const getPrivateLobbyRoomToken = ({
    roomId,
}: {
    roomId: string;
}): Promise<AxiosResponse<any, any>> => {
    return tokenInstance().get(`/rooms/private/${roomId}/lobby-token`);
};

export const getPublicLobbyRoomToken = ({
    roomId,
}: {
    roomId: string;
}): Promise<AxiosResponse<any, any>> => {
    return tokenInstance().get(`/rooms/public/${roomId}/lobby-token`);
};

export const getPrivateRoomsToken = ({
    roomId,
}: {
    roomId: string;
}): Promise<AxiosResponse<any, any>> => {
    return tokenInstance().get(`/rooms/private/${roomId}/token`);
};

export const getPublicRoomsToken = ({
    roomId,
}: {
    roomId: string;
}): Promise<AxiosResponse<any, any>> => {
    return tokenInstance().get(`/rooms/public/${roomId}/token`);
};

export const getCompanyRoomsToken = ({
    roomId,
    companyId,
}: {
    roomId: string;
    companyId: string;
}): Promise<AxiosResponse<any, any>> => {
    return tokenInstance().get(`companies/${companyId}/rooms/${roomId}/token`);
};

export const deleteCompanyRoom = ({
    roomId,
    companyId,
}: {
    roomId: string;
    companyId: string;
}): Promise<AxiosResponse<any, any>> => {
    return tokenInstance().delete(`companies/${companyId}/rooms/${roomId}`);
};

export const deletePrivateRoom = ({
    roomId,
}: {
    roomId: string;
}): Promise<AxiosResponse<any, any>> => {
    return tokenInstance().delete(`rooms/private/${roomId}`);
};

export const deletePublicRoom = ({
    roomId,
}: {
    roomId: string;
}): Promise<AxiosResponse<any, any>> => {
    return tokenInstance().delete(`rooms/public/${roomId}`);
};

export const patchCompanyRoom = ({
    roomId,
    companyId,
    name,
}: {
    roomId: string;
    companyId: string;
    name: string;
}): Promise<AxiosResponse<any, any>> => {
    return tokenInstance().patch(`companies/${companyId}/rooms/${roomId}`, {
        name,
    });
};

export const getBreakoutRoomToken = ({
    roomId,
    companyId,
    name,
}: {
    roomId: string;
    companyId: string;
    name: string;
}): Promise<AxiosResponse<any, any>> => {
    return tokenInstance().get(
        `companies/${companyId}/rooms/${roomId}/breakout/${name}/token`,
    );
};

// * Single ROOM SESSIONS
export const getFocusSessionsToken = ({
    sessionID,
}: {
    sessionID: number;
}): Promise<AxiosResponse<any, any>> => {
    return tokenInstance().get(`/focus-sessions/${sessionID}/token`);
};

export const getFocusSessionsLobbyToken = ({
    sessionID,
}: {
    sessionID: number;
}): Promise<AxiosResponse<any, any>> => {
    return tokenInstance().get(`/focus-sessions/${sessionID}/lobby-token`);
};
// * Single ROOM SESSIONS

// * Meetings ROOM
// * Only used by guest user
export const getMeetingTokenWithMeetingUUIDAndPasscode = ({
    meetingUUID,
    passcode,
    uuid,
}: {
    meetingUUID: string;
    passcode: string;
    uuid: string;
}): Promise<AxiosResponse<any, any>> => {
    return tokenInstance().get(
        `/meetings/${meetingUUID}/guest-token?passcode=${passcode}&uuid=${uuid}`,
    );
};

export const getMeetingLobbyTokenWithMeetingUUIDAndPasscode = ({
    meetingUUID,
    passcode,
}: {
    meetingUUID: string;
    passcode: string;
}): Promise<AxiosResponse<any, any>> => {
    return tokenInstance().get(
        `/meetings/${meetingUUID}/guest-lobby-token?passcode=${passcode}`,
    );
};

export const getMeetingLobbyToken = ({
    meetingUUID,
}: {
    meetingUUID: string;
}): Promise<AxiosResponse<any, any>> => {
    return tokenInstance().get(`/meetings/${meetingUUID}/lobby-token`);
};

export const getTestLobbyToken = (): Promise<AxiosResponse<any, any>> => {
    return tokenInstance().get(`/focus-sessions/test-session/lobby-token`);
};

export const getTestRoomToken = (): Promise<AxiosResponse<any, any>> => {
    return tokenInstance().get(`/focus-sessions/test-session/token`);
};

// * Used by the host
export const getMeetingToken = ({
    meetingUUID,
}: {
    meetingUUID: string;
}): Promise<AxiosResponse<any, any>> => {
    return tokenInstance().get(`/meetings/${meetingUUID}/token`);
};

// * Meeting ROOM

// * GROUP ROOMS
// * The `/rooms/group/` endpoint uses DRF's LimitOffsetPagination (limit/offset
// * query params, not `page`), with a server-side default limit of 100 (see
// * backend `core/settings.py` REST_FRAMEWORK.PAGE_SIZE). Mirror that page size
// * here so a 1-indexed `page` from the UI translates into the `limit`/`offset`
// * pair the backend actually understands.
export const GROUP_ROOMS_PAGE_SIZE = 100;

const getGroupRooms = ({
    search,
    page = 1,
    sort,
    userGroupId,
}: {
    search?: string;
    page?: number;
    sort?: 'active' | 'newest' | 'az';
    userGroupId?: number | string;
}): Promise<AxiosResponse<{
    count: number;
    next: string | null;
    previous: string | null;
    results: IGroupRoom[];
}, any>> => {
    return tokenInstance().get('/rooms/group/', {
        params: {
            search,
            sort,
            user_group_id: userGroupId,
            limit: GROUP_ROOMS_PAGE_SIZE,
            offset: (page - 1) * GROUP_ROOMS_PAGE_SIZE,
        },
    });
};

export const createGroupRoom = ({
    name,
    userGroupId,
}: {
    name: string;
    userGroupId: number;
}): Promise<AxiosResponse<IGroupRoom, any>> => {
    return tokenInstance().post('/rooms/group/', {name, user_group_id: userGroupId});
};

export const updateGroupRoom = ({
    roomId,
    name,
}: {
    roomId: number;
    name: string;
}): Promise<AxiosResponse<IGroupRoom, any>> => {
    return tokenInstance().patch(`/rooms/group/${roomId}`, {name});
};

export const deleteGroupRoom = ({
    roomId,
}: {
    roomId: number;
}): Promise<AxiosResponse<any, any>> => {
    return tokenInstance().delete(`/rooms/group/${roomId}`);
};

export const getGroupRoomToken = ({
    roomId,
}: {
    roomId: string;
}): Promise<AxiosResponse<any, any>> => {
    return tokenInstance().get(`/rooms/group/${roomId}/token`);
};

export const getGroupLobbyRoomToken = ({
    roomId,
}: {
    roomId: string;
}): Promise<AxiosResponse<any, any>> => {
    return tokenInstance().get(`/rooms/group/${roomId}/lobby-token`);
};
// * GROUP ROOMS

// * REPORT USER IN ROOM
// Room-report URLs don't share a shape (private rooms nest differently from
// public/group), so the room_type carried in every LiveKit participant's
// own metadata (see generate_token() in livekit_rtc/models.py, backend
// RoomTypes enum) picks which one to call -- simpler and more robust than
// deriving it from the current route. No COMPANY_ROOM: company room members
// already know each other, so reporting isn't offered there (see
// CompanyRoomsView on the backend, which has no report_user action).
export type ReportableRoomType = 'PUBLIC_ROOM' | 'PRIVATE_ROOM' | 'GROUP_ROOM';

export const reportRoomParticipant = ({
    roomType,
    roomId,
    reportedUser,
    subject,
    info,
}: {
    roomType: ReportableRoomType;
    roomId: number | string;
    reportedUser: number;
    subject: string;
    info: string;
}): Promise<AxiosResponse<any, any>> => {
    const urlByRoomType: Record<ReportableRoomType, string> = {
        PUBLIC_ROOM: `/rooms/public/${roomId}/report-user`,
        PRIVATE_ROOM: `/rooms/private/${roomId}/report-user`,
        GROUP_ROOM: `/rooms/group/${roomId}/report-user`,
    };
    return tokenInstance().post(urlByRoomType[roomType], {
        reported_user: reportedUser,
        subject,
        info,
    });
};
// * REPORT USER IN ROOM

export const RoomsService = {
    handleGetRoomToken,
    getPrivateRooms,
    getPublicRooms,
    postPublicRooms,
    postPrivateRooms,
    getCompanyLobbyRoomToken,
    getCompanyRoomsToken,
    deleteCompanyRoom,
    patchCompanyRoom,
    getPrivateLobbyRoomToken,
    getPublicLobbyRoomToken,
    getPrivateRoomsToken,
    getPublicRoomsToken,
    deletePrivateRoom,
    deletePublicRoom,
    patchPublicRooms,
    patchPrivateRooms,
    getBreakoutRoomToken,
    getFocusSessionsToken,
    getFocusSessionsLobbyToken,
    getMeetingTokenWithMeetingUUIDAndPasscode,
    getMeetingToken,
    getMeetingLobbyTokenWithMeetingUUIDAndPasscode,
    getMeetingLobbyToken,
    getTestLobbyToken,
    getTestRoomToken,
    getGroupRooms,
    createGroupRoom,
    updateGroupRoom,
    deleteGroupRoom,
    getGroupRoomToken,
    getGroupLobbyRoomToken,
    reportRoomParticipant,
};
